// assets/product-personalization.js
// Bloc de personnalisation LIORA (prenoms / initiale a graver).
//
// Affiche le bon nombre de champs selon la variante choisie, conserve ce que le
// client a deja tape lorsque Shopify re-rend la section produit, et empeche
// l'ajout au panier tant que la saisie est incomplete.
//
// Tout est pilote par les metachamps produit (namespace "personnalisation") :
//   - option_source          : nom exact de l'option qui pilote la personnalisation
//   - nb_champs_par_variante : { "valeur d'option": nombre de champs }
//   - type_champ             : texte_libre | selection_variante
//   - ecriture               : arabe | hebreu | vide (ecriture imposee au produit)

/**
 * Saisies du client, conservees HORS du DOM.
 *
 * C'est le coeur du correctif : changer de variante declenche un re-rendu de la
 * section produit par Shopify, qui remplace nos champs. Sans ce cache, le client
 * qui tape "Marie" puis change la couleur du metal perd sa saisie.
 *
 * Cle : `${productId}:${index}`.
 * @type {Map<string, string>}
 */
const savedValues = new Map();

class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || '';
    this.optionSource = this.dataset.optionSource || '';
    this.mode = this.dataset.mode || 'texte_libre';
    this.ecriture = this.dataset.ecriture || '';
    this.separator = this.dataset.separator || ' · ';

    try {
      this.nbMap = JSON.parse(this.dataset.nbMap || '{}');
    } catch {
      this.nbMap = {};
    }

    try {
      this.maxLenMap = JSON.parse(this.dataset.maxlenMap || 'null');
    } catch {
      this.maxLenMap = null;
    }

    try {
      this.symbols = JSON.parse(this.dataset.symboles || 'null');
    } catch {
      this.symbols = null;
    }

    this.fields = Array.from(this.querySelectorAll('[data-perso-field]'));
    this.previewText = this.querySelector('[data-perso-preview-text]');
    this.preview = this.querySelector('[data-perso-preview]');
    this.initialOut = this.querySelector('[data-perso-initial]');

    // Delegue sur document : le variant-picker est lui aussi remplace lors d'un
    // re-rendu, un listener pose directement dessus serait perdu.
    this.onDocumentChange = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (this.contains(target)) return; // nos propres champs
      if (target.matches('[data-option-name], select')) this.sync();
    };
    document.addEventListener('change', this.onDocumentChange);

    this.restoreValues();
    this.bindInputs();
    this.bindKeyboard();
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange);
  }

  /** @returns {Element|null} */
  get picker() {
    return document.querySelector(`variant-picker[data-product-id="${this.productId}"]`);
  }

  /**
   * Valeur actuellement selectionnee pour une option donnee.
   *
   * @param {string} [name] Nom de l'option. Par defaut celle qui pilote la
   *   personnalisation ; la police peut dependre d'une autre.
   * @returns {string|null} null si on ne peut pas conclure (picker absent).
   */
  getSourceValue(name = this.optionSource) {
    const picker = this.picker;
    if (!picker) return null;

    // Style "boutons" : des radios, avec data-option-name pose par le theme.
    if (name) {
      const escaped = window.CSS && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
      const radio = picker.querySelector(
        `input[type="radio"][data-option-name="${escaped}"]:checked`
      );
      if (radio) return radio.value;
    }

    // Style "menus deroulants". On lit select.value et non option[selected] :
    // l'attribut selected reste fige sur la valeur rendue par le serveur.
    for (const select of picker.querySelectorAll('select')) {
      const opt = select.options[select.selectedIndex];
      if (!opt) continue;
      if (!name || opt.dataset.optionName === name) return select.value;
    }

    // Repli : n'importe quelle valeur cochee.
    const any = picker.querySelector('input[type="radio"]:checked');
    return any ? any.value : null;
  }

  /**
   * Nombre de champs a afficher.
   * @returns {number|null} null = indetermine, on conserve l'etat serveur.
   */
  getCount() {
    const value = this.getSourceValue();
    if (value == null) return null;

    if (Object.prototype.hasOwnProperty.call(this.nbMap, value)) {
      const n = parseInt(this.nbMap[value], 10);
      if (Number.isFinite(n) && n >= 0) return n;
    }

    // La valeur d'option porte souvent le nombre elle-meme. On accepte les deux
    // ecritures : libelle court ("3") et libelle long ("3 prenoms").
    //
    // L'ancrage en debut de chaine est volontaire : sans lui, un code de police
    // comme "B16" serait lu comme 16 champs a graver.
    const match = String(value).match(/^\s*(\d+)/);
    if (match) {
      const n = parseInt(match[1], 10);
      if (Number.isFinite(n) && n > 0 && n <= this.fields.length) return n;
    }

    return null;
  }

  restoreValues() {
    this.fields.forEach((field, index) => {
      const input = field.querySelector('input');
      if (!input) return;

      // Ne jamais ecraser le champ en cours de frappe.
      if (document.activeElement === input) return;

      const saved = savedValues.get(`${this.productId}:${index}`);
      if (saved != null && input.value !== saved) input.value = saved;

      this.updateCounter(field, input);
    });
  }

  bindInputs() {
    this.fields.forEach((field, index) => {
      const input = field.querySelector('input');
      if (!input) return;

      input.addEventListener('input', () => {
        savedValues.set(`${this.productId}:${index}`, input.value);
        input.setCustomValidity('');
        field.removeAttribute('data-perso-error');
        this.updateCounter(field, input);
        this.updatePreview();
      });

      // Le clavier de secours ecrit dans le dernier champ touche.
      input.addEventListener('focus', () => {
        this.activeInput = input;
      });

      // Messages de validation en francais, au lieu du texte natif du navigateur.
      input.addEventListener('invalid', () => {
        field.setAttribute('data-perso-error', 'true');
        input.setCustomValidity(this.validationMessage(input, index));
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Clavier de secours                                                     */
  /* ---------------------------------------------------------------------- */

  /**
   * Sur un produit a ecriture imposee, taper suppose d'avoir installe le
   * clavier correspondant sur son telephone. Beaucoup de clientes lisent
   * l'arabe sans l'avoir fait : sans ces touches, elles ne peuvent pas
   * commander, ou pire, saisissent le prenom en lettres latines sur un bijou
   * que le graveur doit rendre en arabe.
   *
   * Le bloc rend les touches masquees : sans JavaScript elles n'ecriraient
   * rien. Elles n'apparaissent donc qu'ici.
   */
  bindKeyboard() {
    this.keyboard = this.querySelector('[data-perso-kbd]');
    if (!this.keyboard) return;

    this.keyboard.hidden = false;

    this.keyboard.addEventListener('click', (event) => {
      const key = event.target.closest('[data-perso-key], [data-perso-erase]');
      if (!key || !this.keyboard.contains(key)) return;

      const input = this.targetInput();
      if (!input) return;

      if (key.hasAttribute('data-perso-erase')) {
        this.eraseAt(input);
      } else {
        this.insertAt(input, key.dataset.persoKey || '');
      }
    });
  }

  /**
   * Champ qui recoit les touches : le dernier touche, sinon le premier visible.
   * Sans ce repli, un premier appui sur une lettre ne saurait pas ou ecrire.
   *
   * @returns {HTMLInputElement|null}
   */
  targetInput() {
    if (this.activeInput && !this.activeInput.disabled && this.contains(this.activeInput)) {
      return this.activeInput;
    }
    const field = this.fields.find((f) => !f.hidden);
    return field ? field.querySelector('input') : null;
  }

  /**
   * Ecrit a la position du curseur, sans redonner le focus au champ.
   *
   * Le focus est volontairement laisse de cote : sur mobile il ouvrirait le
   * clavier natif par-dessus celui-ci. Celle qui a deja son clavier arabe
   * touche le champ et tape normalement, les deux voies coexistent.
   *
   * L'evenement `input` est reemis pour que le compteur, l'apercu, le cache de
   * saisie et le filtre de caracteres de liora.js voient la nouvelle valeur.
   *
   * @param {HTMLInputElement} input
   * @param {string} texte
   */
  insertAt(input, texte) {
    if (!texte) return;

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const max = input.maxLength > 0 ? input.maxLength : Infinity;

    // Place restante une fois la selection remplacee : sans ce calcul, la
    // limite du fournisseur serait franchie par le clavier alors qu'elle est
    // tenue a la frappe.
    const restant = max - (input.value.length - (end - start));
    if (restant <= 0) return;

    const morceau = texte.slice(0, restant);
    input.value = input.value.slice(0, start) + morceau + input.value.slice(end);

    const caret = start + morceau.length;
    try {
      input.setSelectionRange(caret, caret);
    } catch {
      // Certains navigateurs refusent sur un champ non focalise : sans
      // repositionnement, la lettre suivante s'ajoutera a la fin, ce qui reste
      // le comportement attendu dans la quasi-totalite des cas.
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Efface la selection, ou le caractere qui precede le curseur.
   * @param {HTMLInputElement} input
   */
  eraseAt(input) {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;

    let caret;
    if (start !== end) {
      input.value = input.value.slice(0, start) + input.value.slice(end);
      caret = start;
    } else {
      if (start === 0) return;
      input.value = input.value.slice(0, start - 1) + input.value.slice(start);
      caret = start - 1;
    }

    try {
      input.setSelectionRange(caret, caret);
    } catch {
      /* voir insertAt */
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} index
   * @returns {string}
   */
  validationMessage(input, index) {
    const label = `${this.dataset.label || 'Champ'} ${index + 1}`;
    if (input.validity.valueMissing) {
      return `Merci d'indiquer le ${label.toLowerCase()} a graver.`;
    }
    if (input.validity.patternMismatch) {
      // Un message generique sur un produit a ecriture imposee laisserait
      // croire que la saisie latine est presque bonne. Elle est ingravable.
      if (this.ecriture === 'arabe') {
        return 'Ce champ ne grave que des caracteres arabes. Utilisez le clavier affiche juste en dessous si vous ne l’avez pas sur votre appareil.';
      }
      if (this.ecriture === 'hebreu') {
        return 'Ce champ ne grave que des caracteres hebreux. Utilisez le clavier affiche juste en dessous si vous ne l’avez pas sur votre appareil.';
      }
      return 'Seuls les lettres, chiffres, espaces, apostrophes, traits d’union et parentheses peuvent etre graves.';
    }
    if (input.validity.tooLong) {
      return `Ce champ est limite a ${input.maxLength} caracteres.`;
    }
    return 'Merci de verifier ce champ.';
  }

  updateCounter(field, input) {
    const counter = field.querySelector('[data-perso-count]');
    if (counter && input.maxLength > 0) {
      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }
  }

  /**
   * Les codes fournisseur deviennent le signe qu'ils representent — dans
   * l'apercu SEULEMENT.
   *
   * Le champ de saisie et la propriete de commande gardent « (M01) » : c'est ce
   * code que le graveur attend, un caractere unicode ne lui dirait rien. La
   * cliente, elle, doit voir un coeur, sinon elle ne comprend pas ce qu'elle
   * achete. Les deux verites coexistent, chacune a sa place.
   *
   * Les parentheses sont obligatoires : sans elles, un prenom comme « M01ka »
   * verrait son code remplace au milieu du mot.
   *
   * @param {string} texte
   * @returns {string}
   */
  renderSymbols(texte) {
    if (!this.symbols || typeof this.symbols !== 'object') return texte;

    return texte.replace(/\(\s*([A-Za-z]\d{1,3})\s*\)/g, (brut, code) => {
      const signe = this.symbols[code.toUpperCase()];
      return typeof signe === 'string' && signe !== '' ? signe : brut;
    });
  }

  updatePreview() {
    if (!this.previewText) return;

    const parts = this.fields
      .filter((field) => !field.hidden)
      .map((field) => field.querySelector('input'))
      .filter((input) => input && input.value.trim() !== '')
      .map((input) => input.value.trim());

    if (parts.length === 0) {
      if (this.preview) this.preview.hidden = true;
      this.previewText.textContent = '';
      return;
    }

    if (this.preview) this.preview.hidden = false;
    this.previewText.textContent = this.renderSymbols(parts.join(this.separator));
  }

  /**
   * Police du graveur, quand elle depend de la variante.
   *
   * Le serveur rend deja la bonne valeur, puisque changer de variante re-rend la
   * section. Ce filet couvre l'intervalle avant que ce re-rendu n'arrive : une
   * gravure partie dans la mauvaise police ne se rattrape pas, contrairement a
   * un nombre de champs momentanement faux.
   *
   * Sans carte par variante, on ne touche a rien : la police est fixe.
   */
  syncPolice() {
    const input = document.querySelector('[data-perso-police]');
    if (!input) return;

    const name = input.dataset.policeOption || '';
    if (!name) return;

    let map = null;
    try {
      map = JSON.parse(input.dataset.policeMap || 'null');
    } catch {
      map = null;
    }
    if (!map || typeof map !== 'object') return;

    const value = this.getSourceValue(name);
    if (value == null) return;

    if (Object.prototype.hasOwnProperty.call(map, value)) {
      input.value = map[value];
    }
  }

  /**
   * Longueur maximale du champ, quand elle depend de la variante.
   *
   * Un collier vendu « 3 lettres » ne doit pas accepter dix caracteres : le
   * palier de prix repose entierement sur cette limite. On tronque aussi la
   * saisie deja tapee, sinon on repart en arriere avec un texte trop long qui
   * partirait tel quel a la commande.
   *
   * Sans carte, on ne touche a rien : la limite est fixe.
   */
  syncMaxLen() {
    if (!this.maxLenMap || typeof this.maxLenMap !== 'object') return;

    const value = this.getSourceValue();
    if (value == null) return;
    if (!Object.prototype.hasOwnProperty.call(this.maxLenMap, value)) return;

    const max = parseInt(this.maxLenMap[value], 10);
    if (!Number.isFinite(max) || max < 1) return;

    this.fields.forEach((field, index) => {
      const input = field.querySelector('input');
      if (!input) return;

      input.maxLength = max;

      if (input.value.length > max) {
        input.value = input.value.slice(0, max);
        savedValues.set(`${this.productId}:${index}`, input.value);
      }

      this.updateCounter(field, input);
    });
  }

  sync() {
    this.syncPolice();
    this.syncMaxLen();

    // Initiale choisie via une option : on reflete simplement la lettre retenue.
    if (this.mode === 'selection_variante') {
      const value = this.getSourceValue();
      if (value && this.initialOut) this.initialOut.textContent = value;
      return;
    }

    const count = this.getCount();

    // Indetermine : on garde ce que Liquid a rendu cote serveur. Ne jamais
    // tomber a zero, sinon le client pourrait acheter sans rien personnaliser.
    if (count === null) {
      this.updatePreview();
      return;
    }

    this.fields.forEach((field, index) => {
      const input = field.querySelector('input');
      if (!input) return;

      const visible = index < count;
      field.hidden = !visible;

      // disabled (et pas seulement hidden) : un champ desactive n'est pas soumis,
      // donc pas de propriete vide "Prenom 4: " sur la commande. La valeur est
      // conservee telle quelle pour le cas ou le client revient en arriere.
      input.disabled = !visible;
      input.required = visible;

      if (visible) {
        input.setAttribute('aria-required', 'true');
      } else {
        input.removeAttribute('aria-required');
        input.setCustomValidity('');
        field.removeAttribute('data-perso-error');
      }
    });

    this.updatePreview();
  }
}

if (!customElements.get('product-personalization-component')) {
  customElements.define('product-personalization-component', ProductPersonalization);
}
